import { Button, Flex, Input, message, Modal } from "antd";
import { HolderOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";

import ConfirmRemoveTagModal from "./ConfirmRemoveTagModal";

import close from "../../assets/icons/close-circle.svg";
import { controller } from "./controller";
import { paletteForTag } from "./lib/tagColors";

export default function ManageTagsModal({ setShowManageTagsModal, showManageTagsModal, allTags, getAllTags }) {
  const [newTagName, setNewTagName] = useState("");
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [showConfirmRemoveModal, setShowConfirmRemoveModal] = useState(false);
  const [tagToRemove, setTagToRemove] = useState({});

  // Local drag-reorder list of the office's custom (non-default) tags. Seeded
  // from `allTags` (already in backend `position` order) and kept in sync after
  // create / delete / reorder refetches.
  const [orderedTags, setOrderedTags] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  useEffect(() => {
    setOrderedTags(allTags.filter((tag) => !tag.is_default_tag));
  }, [allTags]);

  function handleDragStart(index) {
    setDragIndex(index);
  }

  // Live-reorder as the dragged row passes over its neighbours, so the list
  // reflows under the cursor instead of only snapping on drop.
  function handleDragOver(e, overIndex) {
    e.preventDefault();
    if (dragIndex === null || dragIndex === overIndex) return;
    setOrderedTags((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(overIndex, 0, moved);
      return next;
    });
    setDragIndex(overIndex);
  }

  async function handleDrop() {
    setDragIndex(null);
    try {
      setIsSavingOrder(true);
      const orderedIds = orderedTags.map((t) => t.id);
      const response = await controller.reorderTags(orderedIds);
      if (response.status >= 200 && response.status < 300) {
        getAllTags();
      } else {
        message.error("Could not save the new tag order");
      }
    } catch (err) {
      message.error("Could not save the new tag order");
      console.error(err);
    } finally {
      setIsSavingOrder(false);
    }
  }

  async function handleCreateTag() {
    try {
      setIsCreatingTag(true);

      const response = await controller.createTag(newTagName);

      if (response.status === 201) {
        setShowManageTagsModal(false);
        setNewTagName("");
        message.success("Tag created successfully");
        getAllTags();
      }
    } catch (err) {
      message.error("Something went wrong while creating tag");
      console.error(err);
    } finally {
      setIsCreatingTag(false);
    }
  }

  return (
    <Modal title="Manage Tags" open={showManageTagsModal} onCancel={() => setShowManageTagsModal(false)} footer={null} style={{ marginTop: 100 }}>
      <Flex vertical gap={20}>
        <Flex vertical gap={10}>
          <label>Tag Name</label>
          <Input placeholder="Enter New Tag" onChange={(e) => setNewTagName(e.target.value)} value={newTagName} disabled={isCreatingTag} />
        </Flex>

        <Flex vertical align="stretch" gap={10}>
          <label style={{ color: "#8c8c8c", fontSize: 12 }}>
            {orderedTags.length > 0 ? "Drag to reorder how tags appear" : "No custom tags yet."}
          </label>

          <Flex vertical gap={8} style={{ width: "100%" }}>
            {orderedTags.map((tag, index) => {
              const palette = paletteForTag(tag.name);
              return (
                <div
                  key={tag.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={handleDrop}
                  onDragEnd={() => setDragIndex(null)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 10,
                    padding: "6px 10px",
                    borderRadius: 8,
                    border: "1px solid #efeef7",
                    backgroundColor: dragIndex === index ? "#f5f3fb" : "#ffffff",
                    cursor: "grab",
                    opacity: isSavingOrder ? 0.7 : 1,
                  }}
                >
                  <Flex align="center" gap={10}>
                    <HolderOutlined style={{ color: "#b0a8c9" }} />
                    <span
                      style={{
                        padding: "2px 12px",
                        borderRadius: 100,
                        fontSize: 12,
                        backgroundColor: palette ? palette.backgroundColor : "#EEEDFA",
                        border: `1px solid ${palette ? palette.borderColor : "transparent"}`,
                        color: palette ? palette.color : "#6B43B5",
                      }}
                    >
                      {tag.name}
                    </span>
                  </Flex>
                  <img
                    src={close}
                    alt="remove"
                    style={{ cursor: "pointer", width: 15, height: 15 }}
                    onClick={() => {
                      setShowConfirmRemoveModal(true);
                      setTagToRemove(tag);
                    }}
                  />
                </div>
              );
            })}
          </Flex>
        </Flex>

        <Button type="primary" onClick={() => handleCreateTag()} loading={isCreatingTag} disabled={!newTagName}>
          Add
        </Button>
      </Flex>

      <ConfirmRemoveTagModal
        setShowConfirmRemoveModal={setShowConfirmRemoveModal}
        showConfirmRemoveModal={showConfirmRemoveModal}
        setTagToRemove={setTagToRemove}
        getAllTags={getAllTags}
        tag={tagToRemove}
      />
    </Modal>
  );
}
